import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Input,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tabs,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { ControlTowerSectionCard } from '@/components/control-tower/ControlTowerSectionCard';
import { ControlTowerMetricStrip } from '@/components/control-tower/ControlTowerMetricStrip';
import { ControlTowerMetricTile } from '@/components/control-tower/ControlTowerMetricTile';
import { ControlTowerSubQueuePanel } from '@/components/control-tower/ControlTowerSubQueuePanel';
import { EmptyQueueState } from './EmptyQueueState';
import type {
  QuotationAgingBucket,
  QuotationConversionQueueRow,
  QuotationConversionQueuesResult,
} from '@/services/salesDashboard.service';

const { Text } = Typography;

const agingColor: Record<QuotationAgingBucket, string> = {
  '0-7': 'green',
  '8-14': 'gold',
  '15-30': 'orange',
  '30+': 'red',
};

function downloadCsv(filename: string, rows: QuotationConversionQueueRow[]) {
  const headers = [
    'quotation_number',
    'customer_name',
    'commercial_stage_label',
    'days_since_accepted',
    'aging_bucket',
    'grand_total',
    'owner_name',
    'sales_team_name',
    'acceptance_date',
    'valid_until',
    'production_requests',
    'opportunity_number',
    'contract_review_status',
  ];
  const escape = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = [
    headers.join(','),
    ...rows.map((r) =>
      [
        r.quotation_number,
        r.customer_name,
        r.commercial_stage_label,
        r.days_since_accepted,
        r.aging_bucket,
        r.grand_total,
        r.owner_name,
        r.sales_team_name,
        r.acceptance_date,
        r.valid_until,
        r.production_requests.map((p) => p.request_number).join('; '),
        r.opportunity_number,
        r.contract_review_status,
      ]
        .map(escape)
        .join(',')
    ),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export const QuotationConversionZone: React.FC<{
  data?: QuotationConversionQueuesResult;
  loading?: boolean;
  error?: string | null;
  formatCurrency: (v: number) => string;
  onFiltersChange?: (filters: {
    search?: string;
    aging_bucket?: QuotationAgingBucket;
  }) => void;
}> = ({ data, loading, error, formatCurrency, onFiltersChange }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [aging, setAging] = useState<QuotationAgingBucket | undefined>();
  const [activeTab, setActiveTab] = useState<'awaiting' | 'pr' | 'all'>('awaiting');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const kpis = data?.kpis;

  const filterLocal = (rows: QuotationConversionQueueRow[]) => {
    let out = rows;
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter(
        (r) =>
          r.quotation_number.toLowerCase().includes(q) ||
          r.customer_name.toLowerCase().includes(q) ||
          (r.owner_name ?? '').toLowerCase().includes(q)
      );
    }
    if (aging) out = out.filter((r) => r.aging_bucket === aging);
    return out;
  };

  const awaiting = useMemo(
    () => filterLocal(data?.awaiting_customer_order ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- search/aging intentional
    [data?.awaiting_customer_order, search, aging]
  );
  const prAwaiting = useMemo(
    () => filterLocal(data?.won_pr_awaiting_so ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.won_pr_awaiting_so, search, aging]
  );
  const allRows = useMemo(() => [...awaiting, ...prAwaiting], [awaiting, prAwaiting]);

  const columns: ColumnsType<QuotationConversionQueueRow> = [
    {
      title: 'Quotation',
      dataIndex: 'quotation_number',
      key: 'quotation_number',
      width: 160,
      ellipsis: true,
      render: (v: string, r) => <a onClick={() => navigate(r.link_path)}>{v}</a>,
    },
    {
      title: 'Customer',
      dataIndex: 'customer_name',
      key: 'customer_name',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Days open',
      dataIndex: 'days_since_accepted',
      key: 'days_since_accepted',
      width: 110,
      sorter: (a, b) => a.days_since_accepted - b.days_since_accepted,
      defaultSortOrder: 'descend',
      render: (d: number, r) => (
        <Tag color={agingColor[r.aging_bucket]} style={{ marginInlineEnd: 0 }}>
          {d}d · {r.aging_bucket}
        </Tag>
      ),
    },
    {
      title: 'Value',
      dataIndex: 'grand_total',
      key: 'grand_total',
      align: 'right',
      width: 130,
      sorter: (a, b) => a.grand_total - b.grand_total,
      render: (v: number) => (
        <span style={{ whiteSpace: 'nowrap' }}>{formatCurrency(v ?? 0)}</span>
      ),
    },
    {
      title: 'Owner',
      dataIndex: 'owner_name',
      key: 'owner_name',
      width: 130,
      ellipsis: true,
      render: (v) => v || '—',
    },
    {
      title: 'Team',
      dataIndex: 'sales_team_name',
      key: 'sales_team_name',
      width: 110,
      ellipsis: true,
      render: (v) => v || '—',
    },
    {
      title: 'Validity',
      key: 'validity',
      width: 130,
      ellipsis: true,
      render: (_: unknown, r) =>
        r.is_expired ? (
          <Tag color="red" title={`Expired ${r.valid_until}`} style={{ marginInlineEnd: 0 }}>
            Expired
          </Tag>
        ) : r.valid_until ? (
          <span style={{ whiteSpace: 'nowrap' }}>{r.valid_until}</span>
        ) : (
          '—'
        ),
    },
    {
      title: 'PR / risks',
      key: 'risks',
      width: 160,
      render: (_: unknown, r) => {
        const prs = r.production_requests.slice(0, 2);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            {prs.length > 0 ? (
              <Space size={4} wrap={false} style={{ maxWidth: '100%' }}>
                {prs.map((p) => (
                  <a
                    key={p.id}
                    onClick={() => navigate(p.link_path)}
                    style={{ whiteSpace: 'nowrap' }}
                    title={p.request_number}
                  >
                    {p.request_number}
                  </a>
                ))}
                {r.production_requests.length > 2 ? (
                  <Text type="secondary">+{r.production_requests.length - 2}</Text>
                ) : null}
              </Space>
            ) : (
              <Text type="secondary">—</Text>
            )}
            {!r.has_contract_review ? (
              <Tag color="orange" style={{ marginInlineEnd: 0, width: 'fit-content' }}>
                No CR
              </Tag>
            ) : null}
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_: unknown, r) => (
        <Space size={0} wrap={false}>
          <Button type="link" size="small" onClick={() => navigate(r.link_path)}>
            Open
          </Button>
          <Button type="link" size="small" onClick={() => navigate(r.convert_to_so_path)}>
            Convert SO
          </Button>
        </Space>
      ),
    },
  ];

  if (loading && !data) {
    return (
      <ControlTowerSectionCard
        zone="quotationConversion"
        sectionId="quotation-conversion"
        title="Quotation conversion"
      >
        <Spin />
      </ControlTowerSectionCard>
    );
  }

  if (error) {
    return (
      <ControlTowerSectionCard
        zone="quotationConversion"
        sectionId="quotation-conversion"
        title="Quotation conversion"
      >
        <Alert type="warning" message={error} />
      </ControlTowerSectionCard>
    );
  }

  if (!data || !kpis) return null;

  const table = (rows: QuotationConversionQueueRow[], empty: string) =>
    rows.length === 0 ? (
      <EmptyQueueState message={empty} />
    ) : (
      <Table
        rowKey="id"
        size="small"
        tableLayout="fixed"
        pagination={{
          current: page,
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total, range) => `${range[0]}–${range[1]} of ${total}`,
          onChange: (nextPage, nextSize) => {
            setPage(nextPage);
            if (nextSize && nextSize !== pageSize) {
              setPageSize(nextSize);
              setPage(1);
            }
          },
        }}
        dataSource={rows}
        columns={columns}
        scroll={{ x: 1280 }}
      />
    );

  return (
    <ControlTowerSectionCard
      zone="quotationConversion"
      sectionId="quotation-conversion"
      title={`Quotation conversion (${kpis.open_commercial_risk_count})`}
    >
      <ControlTowerMetricStrip zone="quotationConversion" title="Commercial conversion">
        <Col xs={24} sm={12} md={8}>
          <ControlTowerMetricTile
            title="Awaiting customer order"
            count={kpis.awaiting_customer_order_count}
            amount={kpis.awaiting_customer_order_value}
            formatCurrency={formatCurrency}
            urgency={kpis.awaiting_customer_order_count > 0 ? 'attention' : 'neutral'}
            footerLink={{
              label: 'View queue',
              onClick: () => {
                setActiveTab('awaiting');
                document.getElementById('quotation-conversion')?.scrollIntoView({ behavior: 'smooth' });
              },
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <ControlTowerMetricTile
            title="Won — PR booked, awaiting SO"
            count={kpis.won_pr_awaiting_so_count}
            amount={kpis.won_pr_awaiting_so_value}
            formatCurrency={formatCurrency}
            urgency={kpis.won_pr_awaiting_so_count > 0 ? 'critical' : 'neutral'}
            footerLink={{
              label: 'View queue',
              onClick: () => {
                setActiveTab('pr');
                document.getElementById('quotation-conversion')?.scrollIntoView({ behavior: 'smooth' });
              },
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <ControlTowerMetricTile
            title="Open commercial risk"
            count={kpis.open_commercial_risk_count}
            amount={kpis.open_commercial_risk_value}
            formatCurrency={formatCurrency}
            urgency={kpis.max_days_since_accepted > 30 ? 'critical' : 'attention'}
            extra={
              <Text type="secondary" style={{ fontSize: 12 }}>
                Avg {kpis.avg_days_since_accepted ?? '—'}d · Max {kpis.max_days_since_accepted}d
              </Text>
            }
          />
        </Col>
      </ControlTowerMetricStrip>

      <Space wrap style={{ marginBottom: 12, marginTop: 12 }}>
        <Input.Search
          allowClear
          placeholder="Search quotation / customer / owner"
          style={{ width: 280 }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          onSearch={(v) => onFiltersChange?.({ search: v || undefined, aging_bucket: aging })}
        />
        <Select
          allowClear
          placeholder="Aging"
          style={{ width: 120 }}
          value={aging}
          onChange={(v) => {
            setAging(v);
            setPage(1);
            onFiltersChange?.({ search: search || undefined, aging_bucket: v });
          }}
          options={[
            { value: '0-7', label: '0–7 days' },
            { value: '8-14', label: '8–14 days' },
            { value: '15-30', label: '15–30 days' },
            { value: '30+', label: '30+ days' },
          ]}
        />
        <Button
          size="small"
          onClick={() =>
            downloadCsv(
              `quotation-conversion-${activeTab}.csv`,
              activeTab === 'awaiting' ? awaiting : activeTab === 'pr' ? prAwaiting : allRows
            )
          }
        >
          Export CSV
        </Button>
      </Space>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Accepted quotations without a customer order. Production request booked = commercially won (still convert to SO). Closed/cancelled PRs do not count."
      />

      <Tabs
        size="small"
        activeKey={activeTab}
        onChange={(k) => {
          setActiveTab(k as 'awaiting' | 'pr' | 'all');
          setPage(1);
        }}
        items={[
          {
            key: 'awaiting',
            label: `Awaiting order (${awaiting.length})`,
            children: (
              <ControlTowerSubQueuePanel
                zone="quotationAwaitingOrder"
                title="Accepted — awaiting customer order"
                count={awaiting.length}
              >
                {table(
                  awaiting,
                  'No accepted quotations waiting for a customer order. Create SO when PO arrives, or book a production request if manufacturing should start.'
                )}
              </ControlTowerSubQueuePanel>
            ),
          },
          {
            key: 'pr',
            label: `Won — PR booked (${prAwaiting.length})`,
            children: (
              <ControlTowerSubQueuePanel
                zone="quotationPrAwaitingSo"
                title="Won (PR booked) — awaiting sales order"
                count={prAwaiting.length}
              >
                {table(
                  prAwaiting,
                  'No production-request bookings awaiting a sales order. Manufacturing may already have started — chase customer PO and convert to SO.'
                )}
              </ControlTowerSubQueuePanel>
            ),
          },
          {
            key: 'all',
            label: `All open (${allRows.length})`,
            children: (
              <ControlTowerSubQueuePanel zone="quotationConversionAll" title="All open commercial" count={allRows.length}>
                {table(allRows, 'No open commercial conversion risk right now.')}
              </ControlTowerSubQueuePanel>
            ),
          },
        ]}
      />
    </ControlTowerSectionCard>
  );
};
